// @cntrl/api-client — the typed HTTP client over the generated OpenAPI types. The generated
// `schema.ts` is committed and drift-checked; this module wraps it with openapi-fetch (a thin typed fetch)
// and re-exports the handful of named types the app + realtime layers key on. Per we do NOT
// auto-generate TanStack hooks — the hand-written per-domain hooks (in apps/core) are the tenant-keyed
// cache enforcement point.
import createClient, { type Middleware } from "openapi-fetch";
import type { paths, components } from "../generated/schema.js";

export type { paths, components } from "../generated/schema.js";
export type { Middleware } from "openapi-fetch";

/** The openapi-fetch client instance type, specialized to the CNTRL API surface. */
export type ApiClient = ReturnType<typeof createClient<paths>>;

/** Options for {@link createApiClient}. `baseUrl` comes from injected __ENV__ (never a literal).*/
export interface CreateApiClientOptions {
  /**
   * The API ORIGIN, e.g. `API_URL` from injected __ENV__ ("" for same-origin dev). The OpenAPI paths
   * already carry the `/api/v1/...` prefix, so this is the origin only, never `${API_URL}/api/v1`.
   */
  baseUrl: string;
  /** Middlewares (auth-header injection, 401→clear-tokens) — supplied by apps/core over the TokenProvider. */
  middlewares?: Middleware[];
}

/**
 * Build the typed API client. Cookies (the httpOnly reissue cookie, T-08) ride automatically same-origin;
 * the bearer is injected by an auth middleware the app supplies. `credentials: "include"` so the
 * Path-scoped reissue cookie is sent to `/auth/reissue`.
 */
export function createApiClient(options: CreateApiClientOptions): ApiClient {
  const client = createClient<paths>({
    baseUrl: options.baseUrl,
    credentials: "include",
  });
  for (const mw of options.middlewares ?? []) client.use(mw);
  return client;
}

// ---- The plane-B realtime payload types (registered via the OpenAPI DocumentFilter) ---------
// The SPA's signal→invalidation registry is typed against these. Dash-form `type` vocabulary.

/** `{ type, id, v, t }` — the frozen UI invalidation signal.*/
export type UiSignal = components["schemas"]["UiSignal"];

/** The reconnect join-ack; `versions` reserved-empty at go-live.*/
export type UiHelloFrame = components["schemas"]["UiHelloFrame"];

/** The dash-form signal `type` union (guests-changed | guest-scanned | …), from the frozen enum. */
export type UiSignalType = NonNullable<UiSignal["type"]>;
